/**
 * Carrossel de vídeos — HTML e CSS compartilhados entre o palco, a visualização e o Moodle.
 * A fileira usa o mesmo motor do carrossel de cards (Netflix ou Youtube).
 */
import { CARD_SLIDER_BOOT, CARD_SLIDER_MAX_PER_PAGE } from './cardSliderWidget';

export const VIDEO_SLIDER_MAX = 12;

function clampInt(value, min, max, fallback) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function escText(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escAttr(value) {
  return escText(value).replace(/"/g, '&quot;');
}

function asHttps(url) {
  const raw = String(url || '').trim();
  if (/^https:\/\//i.test(raw)) return raw;
  if (/^http:\/\//i.test(raw)) return `https://${raw.slice(7)}`;
  return '';
}

function readEmbedSource(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^\s*<iframe\b/i.test(text)) {
    const srcMatch = text.match(/src\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i);
    return srcMatch ? (srcMatch[1] || srcMatch[2] || srcMatch[3] || '') : '';
  }
  return text;
}

/** YouTube, Vimeo ou link de incorporação do Panda. */
export function videoSourceInfo(raw) {
  const source = asHttps(readEmbedSource(raw));
  if (!source) return null;
  const yt = source.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:shorts\/|live\/|embed\/|watch\?(?:.*&)?v=|v\/|e\/))([A-Za-z0-9_-]{11})/i);
  if (yt) return { provider: 'youtube', id: yt[1], src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = source.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return { provider: 'vimeo', id: vimeo[1], src: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/pandavideo|player-vz-|b-vz-|panda\.video/i.test(source)) return { provider: 'panda', id: '', src: source };
  return null;
}

/** Largura / altura. 0 significa altura fixa em pixels. */
export function videoFrameAspect(aspect) {
  if (aspect === '4:3') return 4 / 3;
  if (aspect === '1:1') return 1;
  if (aspect === '9:16') return 9 / 16;
  if (aspect === 'fixed') return 0;
  return 16 / 9;
}

/** Devolve a URL do iframe. */
export function videoEmbedSrc(raw, autoplay) {
  const info = videoSourceInfo(raw);
  if (!info) return '';
  let src = info.src;
  const flag = autoplay ? '1' : '0';
  src = src.replace(/([?&])autoplay=\d/gi, `$1autoplay=${flag}`);
  if (!/[?&]autoplay=/.test(src)) src += `${src.includes('?') ? '&' : '?'}autoplay=${flag}`;
  if (info.provider === 'youtube') {
    if (!/[?&]enablejsapi=/.test(src)) src += '&enablejsapi=1';
    if (!/[?&]rel=/.test(src)) src += '&rel=0';
  }
  return src;
}

export const VIDEO_SLIDER_CSS = `
.madri-video-slider{position:relative;box-sizing:border-box;padding:8px 0 4px;max-width:100%}
.madri-video-slider__viewport{overflow:hidden !important;width:100%}
.madri-video-slider__track{display:flex !important;flex-wrap:nowrap !important;align-items:stretch;transition:transform .45s ease;will-change:transform}
.madri-video-slider__card{box-sizing:border-box;display:flex !important;flex-direction:column;min-width:0;max-width:none !important;background:var(--vs-card,#fff);border-radius:var(--vs-card-radius,14px);overflow:hidden}
.madri-video-slider--shadow .madri-video-slider__card{box-shadow:0 10px 24px rgba(15,23,42,.12)}
.madri-video-slider--lift .madri-video-slider__card{transition:transform .2s ease,box-shadow .2s ease}
.madri-video-slider--lift .madri-video-slider__card:hover{transform:translateY(-6px)}
.madri-video-slider__frame{position:relative;width:100%;height:auto;aspect-ratio:var(--vs-aspect,1.7778);overflow:hidden;border-radius:var(--vs-video-radius,12px);background:#0f172a;flex:0 0 auto}
.madri-video-slider--ratio-fixed .madri-video-slider__frame{height:var(--vs-img,180px);aspect-ratio:auto}
.madri-video-slider__cover,.madri-moodle-layout .madri-video-slider__cover{position:absolute;inset:0;display:block !important;width:100% !important;max-width:none !important;height:100% !important;object-fit:cover !important;border-radius:0 !important}
.madri-video-slider__cover:not([src]),.madri-video-slider__cover[src=""]{display:none !important}
.madri-video-slider__frame iframe,.madri-video-slider__poster{position:absolute;inset:0;width:100% !important;height:100% !important;max-width:none !important;border:0}
.madri-video-slider__poster{pointer-events:none}
.madri-video-slider__play{position:absolute;inset:0;z-index:2;width:100%;height:100%;margin:0;padding:0;border:0;border-radius:0;background:transparent;box-shadow:none;cursor:pointer;display:flex;align-items:center;justify-content:center}
.madri-video-slider__glyph{width:54px;height:54px;border-radius:999px;background:var(--vs-play,#fff);color:var(--vs-play-text,#0f172a);box-shadow:0 8px 20px rgba(15,23,42,.28);font-size:22px;line-height:54px;text-align:center;pointer-events:none}
.madri-video-slider__title,.madri-moodle-layout .madri-video-slider__title{margin:0;padding:12px 14px 14px;color:var(--vs-title,#0f172a) !important;font-size:15px;font-weight:700;line-height:1.35}
.madri-video-slider__arrow{position:absolute;top:var(--vs-arrow-y,30%);transform:translateY(-50%);z-index:3;width:36px;height:36px;border:0;border-radius:999px;background:var(--vs-arrow,#fff);color:var(--vs-arrow-text,#0f172a);box-shadow:0 4px 14px rgba(15,23,42,.18);cursor:pointer;font-size:22px;line-height:1;display:flex;align-items:center;justify-content:center}
.madri-video-slider__arrow:disabled{opacity:.35;cursor:default}
.madri-video-slider__arrow--prev{left:4px}
.madri-video-slider__arrow--next{right:4px}
.madri-video-slider__dots{display:flex;justify-content:center;gap:6px;margin-top:12px}
.madri-video-slider__dot{width:8px;height:8px;padding:0;border:0;border-radius:999px;background:var(--vs-dot,#cbd5e1);cursor:pointer}
.madri-video-slider__dot.is-active{width:18px;background:var(--vs-dot-on,#2563eb)}
.madri-video-slider__modal{position:fixed !important;inset:0 !important;z-index:1000002 !important;background:rgba(15,23,42,.72);display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box}
.madri-video-slider__modal[hidden]{display:none !important}
.madri-video-slider__dialog{position:relative;width:min(960px,100%);aspect-ratio:16/9;background:#000;border-radius:12px}
.madri-video-slider__stage{position:absolute;inset:0}
.madri-video-slider__stage iframe{width:100%;height:100%;border:0}
.madri-video-slider__close{position:absolute;top:-12px;right:-12px;z-index:1;width:36px;height:36px;border:0;border-radius:999px;background:#fff;color:#0f172a;cursor:pointer;font-size:20px;line-height:1}
@media (prefers-reduced-motion:reduce){.madri-video-slider__track,.madri-video-slider--lift .madri-video-slider__card{transition:none}}
`.replace(/\s+/g, ' ').trim();

const VIDEO_SLIDER_PLAY = `<script>
(function(){
  function withPlay(src) {
    var u = String(src || '');
    if (!u) return '';
    u = u.replace(/([?&])autoplay=0/gi, '$1autoplay=1');
    if (!/[?&]autoplay=1/.test(u)) u += (u.indexOf('?') >= 0 ? '&' : '?') + 'autoplay=1';
    return u;
  }
  function frameHtml(src) {
    return '<iframe src="' + withPlay(src).replace(/"/g, '&quot;') + '" title="" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
  }
  function hold(root, on) {
    if (!root) return;
    if (on) {
      root.setAttribute('data-vs-hold', '1');
      root.dispatchEvent(new Event('mouseenter'));
    } else {
      root.removeAttribute('data-vs-hold');
      root.dispatchEvent(new Event('mouseleave'));
    }
  }
  function modalOf(root) {
    return root._vsModal || root.querySelector('.madri-video-slider__modal');
  }
  function closeModal(root) {
    var modal = modalOf(root);
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    var stage = modal.querySelector('.madri-video-slider__stage');
    if (stage) stage.innerHTML = '';
    if (root._vsOverflow != null) document.body.style.overflow = root._vsOverflow;
    root._vsOverflow = null;
    hold(root, false);
  }
  function openModal(root, src) {
    var modal = modalOf(root);
    var stage = modal && modal.querySelector('.madri-video-slider__stage');
    if (!modal || !stage) return;
    root._vsModal = modal;
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    stage.innerHTML = frameHtml(src);
    modal.hidden = false;
    if (root._vsOverflow == null) root._vsOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }
  function placeArrows(root) {
    var frame = root.querySelector('.madri-video-slider__frame');
    if (!frame) return;
    var top = frame.getBoundingClientRect().height / 2;
    if (top > 8) root.style.setProperty('--vs-arrow-y', top + 'px');
  }
  function fillVimeo(root) {
    var imgs = root.querySelectorAll('[data-vs-vimeo]');
    for (var i = 0; i < imgs.length; i++) {
      (function(img) {
        var id = img.getAttribute('data-vs-vimeo');
        if (!id || img.getAttribute('data-vs-thumb-done')) return;
        img.setAttribute('data-vs-thumb-done', '1');
        fetch('https://vimeo.com/api/oembed.json?url=' + encodeURIComponent('https://vimeo.com/' + id))
          .then(function(r) { return r.json(); })
          .then(function(data) {
            if (data && data.thumbnail_url) img.src = String(data.thumbnail_url).replace(/_[0-9]+x[0-9]+/, '_640');
          })
          .catch(function() {});
      })(imgs[i]);
    }
  }
  function boot(root) {
    if (!root || root.getAttribute('data-vs-ready') === '1') return;
    root.setAttribute('data-vs-ready', '1');
    var modal = root.querySelector('.madri-video-slider__modal');
    if (modal) {
      root._vsModal = modal;
      modal.addEventListener('click', function(ev) {
        if (ev.target === modal || (ev.target.closest && ev.target.closest('[data-vs-close]'))) {
          ev.preventDefault();
          closeModal(root);
        }
      });
    }
    root.addEventListener('click', function(ev) {
      var play = ev.target.closest ? ev.target.closest('[data-vs-play]') : null;
      if (!play || !root.contains(play)) return;
      ev.preventDefault();
      var src = play.getAttribute('data-vs-src');
      if (!src) return;
      hold(root, true);
      if (root.getAttribute('data-open') === 'inline') {
        var frame = play.closest('.madri-video-slider__frame');
        if (frame) frame.innerHTML = frameHtml(src);
        return;
      }
      openModal(root, src);
    });
    document.addEventListener('keydown', function(ev) {
      if (ev.key === 'Escape') closeModal(root);
    });
    fillVimeo(root);
    placeArrows(root);
    window.addEventListener('resize', function() { placeArrows(root); });
    if (window.ResizeObserver) {
      var watch = root.querySelector('[data-cs="viewport"]') || root;
      var observer = new ResizeObserver(function() { placeArrows(root); });
      observer.observe(watch);
    }
  }
  window.madriVideoSliderBoot = boot;
})();
</script>`;

function originCoverHtml(video, title, src) {
  const custom = String(video?.coverUrl || '').trim();
  if (custom) return `<img class="madri-video-slider__cover" src="${escAttr(custom)}" alt="${escAttr(title)}">`;
  const info = videoSourceInfo(video?.videoUrl);
  if (!info) return '';
  if (info.provider === 'youtube') {
    const max = `https://i.ytimg.com/vi/${info.id}/maxresdefault.jpg`;
    const hq = `https://i.ytimg.com/vi/${info.id}/hqdefault.jpg`;
    return `<img class="madri-video-slider__cover" src="${max}" alt="${escAttr(title)}" onerror="this.onerror=null;this.src='${hq}'">`;
  }
  if (info.provider === 'vimeo') {
    return `<img class="madri-video-slider__cover" alt="${escAttr(title)}" data-vs-vimeo="${escAttr(info.id)}">`;
  }
  if (info.provider === 'panda' && src) {
    return `<iframe class="madri-video-slider__poster" src="${escAttr(src)}" title="" tabindex="-1" loading="lazy"></iframe>`;
  }
  return '';
}

function videoInner(video) {
  const title = String(video?.title || '').trim();
  const src = videoEmbedSrc(video?.videoUrl, false);
  const glyph = '<span class="madri-video-slider__glyph">&#9654;</span>';
  const playHtml = src
    ? `<button type="button" class="madri-video-slider__play" data-vs-play data-vs-src="${escAttr(src)}" aria-label="${escAttr(title || 'Play')}">${glyph}</button>`
    : `<span class="madri-video-slider__play" aria-hidden="true">${glyph}</span>`;
  const titleHtml = title ? `<div class="madri-video-slider__title">${escText(title)}</div>` : '';
  return `<div class="madri-video-slider__card"><div class="madri-video-slider__frame">${originCoverHtml(video, title, src)}${playHtml}</div>${titleHtml}</div>`;
}

export function createDefaultVideoCardSliderContent(labels = {}) {
  const video = (title) => ({ title, videoUrl: '', coverUrl: '' });
  return {
    layout: 'netflix',
    perPage: 3,
    perPageMobile: 1,
    gap: 16,
    peek: 56,
    imageHeight: 180,
    aspect: '16:9',
    videoRadius: 12,
    cardRadius: 14,
    openMode: 'modal',
    autoplay: false,
    autoplayMs: 5000,
    loop: true,
    showArrows: true,
    showDots: true,
    shadow: true,
    hoverLift: true,
    cardBg: '#ffffff',
    titleColor: '#0f172a',
    playBg: '#ffffff',
    playColor: '#0f172a',
    arrowBg: '#ffffff',
    arrowColor: '#0f172a',
    dotColor: '#cbd5e1',
    dotActive: '#2563eb',
    videos: [
      video(labels.video1 || 'Aula 1'),
      video(labels.video2 || 'Aula 2'),
      video(labels.video3 || 'Aula 3'),
    ],
  };
}

export function buildVideoCardSliderInnerHtml(content, widgetId) {
  const c = content || {};
  const videos = Array.isArray(c.videos) ? c.videos.slice(0, VIDEO_SLIDER_MAX) : [];
  const layout = c.layout === 'youtube' ? 'youtube' : 'netflix';
  const id = `vs_${String(widgetId || 'videos').replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const aspect = videoFrameAspect(c.aspect);
  const fixedRatio = aspect === 0;
  const vars = [
    `--vs-card:${c.cardBg || '#ffffff'}`,
    `--vs-title:${c.titleColor || '#0f172a'}`,
    `--vs-play:${c.playBg || '#ffffff'}`,
    `--vs-play-text:${c.playColor || '#0f172a'}`,
    `--vs-arrow:${c.arrowBg || '#ffffff'}`,
    `--vs-arrow-text:${c.arrowColor || '#0f172a'}`,
    `--vs-dot:${c.dotColor || '#cbd5e1'}`,
    `--vs-dot-on:${c.dotActive || '#2563eb'}`,
    `--vs-card-radius:${clampInt(c.cardRadius, 0, 40, 14)}px`,
    `--vs-video-radius:${clampInt(c.videoRadius, 0, 40, 12)}px`,
    `--vs-img:${clampInt(c.imageHeight, 80, 420, 180)}px`,
    `--vs-aspect:${fixedRatio ? 1.7778 : aspect}`,
  ].join(';');
  const classes = [
    'madri-video-slider',
    layout === 'youtube' ? 'madri-video-slider--youtube' : 'madri-video-slider--netflix',
    fixedRatio ? 'madri-video-slider--ratio-fixed' : '',
    c.shadow === false ? '' : 'madri-video-slider--shadow',
    c.hoverLift === false ? '' : 'madri-video-slider--lift',
  ].filter(Boolean).join(' ');
  const arrows = c.showArrows === false ? '' : '<button type="button" class="madri-video-slider__arrow madri-video-slider__arrow--prev" data-cs="prev" aria-label="Anterior">&#8249;</button><button type="button" class="madri-video-slider__arrow madri-video-slider__arrow--next" data-cs="next" aria-label="Próximo">&#8250;</button>';
  const dots = c.showDots === false ? '' : '<div class="madri-video-slider__dots" data-cs="dots"></div>';
  const autoplay = c.autoplay ? clampInt(c.autoplayMs, 1200, 20000, 5000) : 0;
  const openMode = c.openMode === 'inline' ? 'inline' : 'modal';
  const modal = '<div class="madri-video-slider__modal" hidden><div class="madri-video-slider__dialog" role="dialog"><button type="button" class="madri-video-slider__close" data-vs-close aria-label="Fechar">&#215;</button><div class="madri-video-slider__stage"></div></div></div>';
  return `<style>${VIDEO_SLIDER_CSS}</style>${CARD_SLIDER_BOOT}${VIDEO_SLIDER_PLAY}<div id="${id}" class="${classes}" style="${escAttr(vars)}" data-layout="${layout}" data-per="${clampInt(c.perPage, 1, CARD_SLIDER_MAX_PER_PAGE, 3)}" data-per-mobile="${clampInt(c.perPageMobile, 1, CARD_SLIDER_MAX_PER_PAGE, 1)}" data-gap="${clampInt(c.gap, 0, 48, 16)}" data-peek="${clampInt(c.peek, 12, 180, 56)}" data-loop="${c.loop === false ? '0' : '1'}" data-autoplay="${autoplay}" data-open="${openMode}" data-cs-dot="madri-video-slider__dot" tabindex="0"><div class="madri-video-slider__viewport" data-cs="viewport"><div class="madri-video-slider__track" data-cs="track">${videos.map((video) => videoInner(video)).join('')}</div></div>${arrows}${dots}${modal}</div><script>if(window.madriCardSliderBoot){window.madriCardSliderBoot(document.getElementById('${id}'));}if(window.madriVideoSliderBoot){window.madriVideoSliderBoot(document.getElementById('${id}'));}</script>`;
}
